<template>
  <div class="col-sm-12 nopads">
    <div class="col-sm-12 editor-form" @click.stop="">
      <h3 class="editor-header">{{ dailyAllowanceUpdate.id > 0 ? $t('daily_allowance.edit') : $t('daily_allowance.add')}}</h3>
      <div class="col-sm-12 form-data nopads">
        <!-- Users -->
        <div
          v-if="dailyAllowanceUpdate.id < 1"
          class="nopads"
        >
          <span class="span-title">
            {{ $t('work_time.user') }}
          </span>
          <select
            v-model="dailyAllowanceUpdate.user.id"
            class="form-select form-select-sm mb-3"
            :disabled="!users || users.length < 1"
          >
            <option
              :value="null"
              disabled
            >
              {{ $t('work_time.select_user') }}
            </option>
            <option
              v-for="item in users"
              :key="item.id"
              :value="item.id"
            >
              {{ item.last_name }}, {{ item.first_name }}
            </option>
          </select>
        </div>
        <div v-else>
          <h3>
            {{ dailyAllowanceUpdate.user.first_name + ' ' + dailyAllowanceUpdate.user.last_name }}
          </h3>
        </div>

        <!-- Start time -->
        <div class="col-sm-6 nopads">
          <span class="span-title">
            {{ $t('work_time.start') }}
          </span>
          <date-time-picker
            v-model:date="dailyAllowanceUpdate.start_time"
            format="dd.MM.yyyy HH:mm"
            :enableTimePicker="true"
            :placeholder="$t('observations_videos.search_input_format')"
          />
        </div>

        <!-- Start place -->
        <div class="col-sm-12 nopads">
          <span class="span-title">
            {{ $t('work_time.start_location') }}
          </span>
          <textarea
            id="start_location"
            v-model="dailyAllowanceUpdate.start_place"
            :placeholder="$t('work_time.start_location')"
            class="form-control"
            :rows="1"
          />
        </div>

        <!-- End time -->
        <div class="col-sm-6 nopads">
          <span class="span-title">
            {{ $t('work_time.end') }}
          </span>
          <date-time-picker
            v-model:date="dailyAllowanceUpdate.end_time"
            format="dd.MM.yyyy HH:mm"
            :enableTimePicker="true"
            :placeholder="$t('observations_videos.search_input_format')"
          />
        </div>

        <!-- end place -->
        <div class="col-sm-12 nopads">
          <span class="span-title">
            {{ $t('work_time.end_location') }}
          </span>
          <textarea
            id="end_location"
            v-model="dailyAllowanceUpdate.end_place"
            class="form-control"
            :placeholder="$t('work_time.end_location')"
            :rows="1"
          />
        </div>

        <!-- Note -->
        <div class="col-sm-12 nopads">
          <span class="span-title">
            {{ $t('work_time.note') }}
          </span>
          <textarea
            id="info"
            v-model="dailyAllowanceUpdate.info"
            class="form-control"
            :placeholder="$t('work_time.note')"
            :rows="4"
          />
        </div>

        <div class="col-sm-12 button-container">
          <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
              {{ $t('common.close') }}
          </button>

          <button v-if="isObserver && dailyAllowanceUpdate.id>0" class="btn btn-primary form-button" :disabled="loading" @click.stop="approveAllowance">
              {{ $t('daily_allowance.approve_allowance') }}
          </button>

          <button class="btn btn-success form-button" :disabled="loading" @click.stop="submit">
              {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {allowanceHelper} from '../mixins/AllowanceMixin'
import DateTimePicker from "@/components/common/DateTimePicker";
import {inject} from "vue";

export default {
    name: 'DailyAllowanceEditor',
    components: { DateTimePicker },
    mixins: [restApi, allowanceHelper],
    props: {
        isObserver: Boolean,
        dailyAllowance: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            users: [],
            dailyAllowanceUpdate: this.dailyAllowance ? this.copyAndFormatObj(this.dailyAllowance) : this.newDailyAllowance(),
            note: '',
            PARTIAL_ALLOWANCE_HOURS: 6
        }
    },

    mounted: function () {
       this.fetchUsers()
    },

    methods: {
        fetchUsers: function () {
            this.restFetch(this.userUrl, this.handleUsers)
        },

        handleUsers: function (response) {
            this.users = response.data
            this.loading = false
        },

        submit: function () {
            if (!this.dailyAllowanceUpdate.start_time) {
                this.showAlert(this.$t('daily_allowance.error_start_missing'))
                return
            }
            if (!this.dailyAllowanceUpdate.end_time) {
                this.showAlert(this.$t('daily_allowance.error_end_missing'))
                return
            }
            let hours = this.diffInHours(this.dailyAllowanceUpdate.end_time, this.dailyAllowanceUpdate.start_time)
            if (hours < this.PARTIAL_ALLOWANCE_HOURS) {
                this.showAlert(this.$t('daily_allowance.not_enough_hours'))
                return
            }
            this.loading = true
            let json = JSON.parse(JSON.stringify(this.dailyAllowanceUpdate))
            if (json.user) {
                json.user = {id: json.user.id}
            }
            if (json.id > 0) {
                this.restUpdate(this.dailyAllowanceUrl, json, this.success, this.fail)
            } else {
                this.restAdd(this.dailyAllowanceUrl, json, this.success, this.fail)
            }
        },

        success: function () {
            this.$emit('closeAndUpdate')
            this.loading = false
        },

        fail: function (response) {
            this.loading = false
            if (response.status === 406) {
                this.showAlert(this.$t('daily_allowance.error_overlapping'))
            } else {
                if (this.dailyAllowanceUpdate.id < 0) {
                    this.showAlert(this.$t('common.alert_add_failed'))
                } else {
                    this.showAlert(this.$t('common.alert_update_failed'))
                }
            }
        },
        approveAllowance: function () {
            this.loading = true
            this.restAdd(this.dailyAllowanceApproveUrl + this.dailyAllowanceUpdate.id, null, this.success, this.fail)
        },
        diffInHours: function (dt2, dt1) {
            var diff = (new Date(dt2).getTime() - new Date(dt1).getTime()) / 1000
            diff /= (60 * 60)
            return diff
        }
    }
}
</script>
